import React, { useState } from 'react';
import { Sparkles, RefreshCw, Loader, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const LabelGeneratorAI = ({ onLabelGenerated }) => {
  const [formData, setFormData] = useState({
    style: 'classico',
    productName: '',
    volume: '500ml',
    alcoholContent: '30%'
  });
  const [logo, setLogo] = useState(null);
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  const styles = [
    { value: 'classico', label: 'Classico', desc: 'Elegante e tradizionale' },
    { value: 'moderno', label: 'Moderno', desc: 'Minimale e contemporaneo' },
    { value: 'vintage', label: 'Vintage', desc: 'Retrò e nostalgico' },
    { value: 'rustico', label: 'Rustico', desc: 'Artigianale e naturale' },
    { value: 'elegante', label: 'Elegante', desc: 'Premium e sofisticato' }
  ];

  const handleGenerate = async () => {
    if (!formData.productName.trim()) {
      toast({
        title: "Errore",
        description: "Inserisci il nome del prodotto",
        variant: "destructive"
      });
      return;
    }

    setGenerating(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formDataToSend = new FormData();
      
      formDataToSend.append('style', formData.style);
      formDataToSend.append('productName', formData.productName);
      formDataToSend.append('volume', formData.volume);
      formDataToSend.append('alcoholContent', formData.alcoholContent);
      
      if (logo) {
        formDataToSend.append('logo', logo);
      }

      const response = await fetch(`${backendUrl}/api/labels/generator/generate-and-save`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Etichetta Generata!",
          description: "La tua etichetta è pronta"
        });
        
        // Chiama callback con il path dell'etichetta
        if (onLabelGenerated) {
          onLabelGenerated(result.filePath);
        }
      } else {
        throw new Error(result.message || 'Errore nella generazione');
      }

    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile generare l'etichetta",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File troppo grande",
          description: "Il logo deve essere massimo 5MB",
          variant: "destructive"
        });
        return;
      }
      setLogo(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-3">
          <Sparkles className="w-6 h-6 text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Genera con AI</h3>
        <p className="text-sm text-gray-600 mt-1">
          Crea un'etichetta professionale in pochi secondi
        </p>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Stile */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stile Etichetta
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {styles.map((style) => (
              <button
                key={style.value}
                type="button"
                onClick={() => setFormData({ ...formData, style: style.value })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.style === style.value
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-200'
                }`}
              >
                <div className="font-medium text-sm">{style.label}</div>
                <div className="text-xs text-gray-500 mt-1">{style.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Nome Prodotto */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome Prodotto *
          </label>
          <input
            type="text"
            required
            value={formData.productName}
            onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
            placeholder="es: Il Mio Amaro"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Volume e Gradazione */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Volume
            </label>
            <input
              type="text"
              value={formData.volume}
              onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
              placeholder="500ml"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gradazione
            </label>
            <input
              type="text"
              value={formData.alcoholContent}
              onChange={(e) => setFormData({ ...formData, alcoholContent: e.target.value })}
              placeholder="30%"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo (opzionale)
          </label>
          <div className="flex items-center gap-3">
            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 transition-colors">
              <Upload className="w-5 h-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">
                {logo ? logo.name : 'Carica logo (max 5MB)'}
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleLogoChange}
                className="hidden"
              />
            </label>
            {logo && (
              <button
                onClick={() => setLogo(null)}
                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
              >
                Rimuovi
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={generating || !formData.productName.trim()}
        className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
      >
        {generating ? (
          <>
            <Loader className="w-5 h-5 mr-2 animate-spin" />
            Generazione in corso...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            Genera Etichetta
          </>
        )}
      </button>
      
      {/* Estimated Time */}
      {generating && (
        <div className="text-center">
          <p className="text-xs text-gray-500">
            ⏱️ Tempo stimato: 20-40 secondi
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Stiamo creando la tua etichetta personalizzata...
          </p>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          💡 <strong>Suggerimento:</strong> L'AI creerà un'etichetta professionale basata sulle tue preferenze. 
          Puoi rigenerare infinite volte fino a trovare il design perfetto!
        </p>
      </div>
    </div>
  );
};

export default LabelGeneratorAI;
